import { useState, useRef, useCallback } from "react";
import { X, ImagePlus, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { uploadImagesToS3 } from "@/services/s3/s3Service";
import { toBase64 } from "@/utils/utils";
import { axiosInstanceWebsite } from "@/services/instance";
import type { ProductInfoCreative } from "../../types/creatives.types";
import { dictionary, Language } from "@/utils/dictionary";
import { sanitizeUploadFilename } from "@/utils/sanitizeUploadFilename";

interface CreateProductModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: (product: ProductInfoCreative) => void;
  language: Language;
}

export function CreateProductModal({
  open,
  onClose,
  onCreated,
  language,
}: CreateProductModalProps) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const t = (key: string, fallback: string) =>
    dictionary[key]?.[language as Language] ?? fallback;

  const resetForm = () => {
    setName("");
    setDescription("");
    setImageFile(null);
    setImagePreview("");
    setError("");
  };

  const handleClose = () => {
    if (!isUploading) {
      resetForm();
      onClose();
    }
  };

  const handleFileSelect = useCallback((file: File) => {
    const validTypes = ["image/jpeg", "image/png", "image/webp"];
    if (!validTypes.includes(file.type)) {
      setError(t("only-jpg-png-webp-allowed", "Only JPG, PNG or WEBP files are allowed"));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError(t("file-max-5mb", "File cannot exceed 5MB"));
      return;
    }
    setError("");
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const file = e.dataTransfer.files[0];
      if (file) handleFileSelect(file);
    },
    [handleFileSelect]
  );

  const handleSubmit = async () => {
    if (!name.trim() || !description.trim() || !imageFile) {
      setError(t("all-fields-required", "Todos los campos son requeridos"));
      return;
    }

    try {
      setIsUploading(true);
      setError("");

      const base64File = await toBase64(imageFile);
      const res = await uploadImagesToS3({
        file: base64File,
        folder: "product-images",
        filename: `${Date.now()}-${sanitizeUploadFilename(imageFile.name)}`,
      });

      const imageUrl = res?.s3_url ?? res?.url ?? res;
      if (typeof imageUrl !== "string" || !imageUrl) {
        throw new Error(t("no-image-url-received", "No image URL received"));
      }

      // Create product in backend
      const productResponse = await axiosInstanceWebsite.post("/store/product", {
        name: name.trim(),
        price: 0,
        available: true,
        images: [imageUrl],
        description: description.trim(),
        category: "ads",
      });

      if (!productResponse.data?.id) {
        throw new Error(t("no-product-id-received", "No product ID received"));
      }

      const newProduct: ProductInfoCreative = {
        product_id: productResponse.data.id,
        website_id: "",
        name: name.trim(),
        description: description.trim(),
        image_url: imageUrl,
      };

      resetForm();
      onCreated(newProduct);
    } catch (err: any) {
      console.error("Error creating product:", err);
      console.error("Response status:", err?.response?.status);
      console.error("Response data:", err?.response?.data);
      console.error("Error message:", err?.message);
      setError(t("error-creating-product", "Error al crear el producto. Intenta de nuevo."));
    } finally {
      setIsUploading(false);
    }
  };

  if (!open) return null;

  const isValid = name.trim() && description.trim() && imageFile;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center"
      data-fluxi-nested-modal
    >
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} />

      <div className="relative bg-background rounded-2xl shadow-2xl border border-border/50 w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="px-6 py-5 border-b border-border/40">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-foreground">
                {t("create-new-product", "Crear producto nuevo")}
              </h2>
              <p className="text-sm text-muted-foreground mt-0.5">
                {t("add-your-product-to-create-ads", "Agrega tu producto para crear anuncios")}
              </p>
            </div>
            <button
              onClick={handleClose}
              disabled={isUploading}
              className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors disabled:opacity-50"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Form */}
        <div className="px-6 py-5 space-y-4">
          {/* Name */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">
              {t("product-name", "Nombre del producto")}
            </label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ej: CeraVe Limpiador Hidratante"
              className="border-border/60"
              disabled={isUploading}
            />
          </div>

          {/* Description */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">
              {t("description", "Descripción")}
            </label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t(
                "describe-your-product",
                "Describe tu producto, beneficios clave, público objetivo..."
              )}
              className="min-h-[80px] resize-none border-border/60"
              disabled={isUploading}
            />
          </div>

          {/* Image upload */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">
              {t("product-image", "Imagen del producto")}
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".jpg,.jpeg,.png,.webp"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleFileSelect(file);
                e.target.value = "";
              }}
            />

            {imagePreview ? (
              <div className="relative w-full h-32 rounded-xl border border-border/60 overflow-hidden bg-muted/20 group">
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="w-full h-full object-contain p-2"
                />
                <button
                  onClick={() => {
                    setImageFile(null);
                    setImagePreview("");
                  }}
                  className="absolute top-2 right-2 w-6 h-6 rounded-full bg-foreground/80 text-background flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => fileInputRef.current?.click()}
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
                className="w-full h-32 rounded-xl border-2 border-dashed border-border/60 hover:border-primary/40 transition-colors flex flex-col items-center justify-center gap-2 text-muted-foreground hover:text-foreground"
              >
                <ImagePlus className="h-6 w-6" />
                <span className="text-xs font-medium">
                  {t("upload-image", "Subir imagen")}
                </span>
                <span className="text-[10px] text-muted-foreground">
                  JPG, PNG, WEBP · Max 5MB
                </span>
              </button>
            )}
          </div>

          {error && <p className="text-xs text-destructive">{error}</p>}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border/40 flex gap-2">
          <Button
            variant="ghost"
            onClick={handleClose}
            disabled={isUploading}
            className="flex-1"
          >
            {t("cancel", "Cancelar")}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!isValid || isUploading}
            className={cn(
              "flex-1 gap-2 font-semibold text-white",
              "disabled:opacity-50"
            )}
            style={{ background: "linear-gradient(to right, #4368E5, #4368E5)" }}
          >
            {isUploading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {t("uploading", "Subiendo...")}
              </>
            ) : (
              t("create-and-continue", "Crear y continuar")
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
